import React from 'react';
import { motion } from 'framer-motion';

const About: React.FC = () => {
  return (
    <div className="py-16 bg-black relative overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-gold-950/30 to-black/90"
        animate={{ 
          backgroundPosition: ['center top', 'center bottom', 'center top'],
        }}
        transition={{ 
          duration: 25, 
          repeat: Infinity, 
          ease: 'linear' 
        }}
        style={{ 
          backgroundSize: 'auto 200%',
        }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="max-w-4xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gold-400 mb-6">About Setora</h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">Setora is a custom software studio for small businesses. We design and build the web apps, mobile apps, and AI-powered tools that growing companies run on — across trades, healthcare, retail, hospitality, finance, and more. Practical software, built around how you actually work.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <motion.div 
            className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-8 shadow-xl shadow-gold-900/20"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-gold-400 mb-4">Our Vision</h2>
            <p className="text-gray-300">Every small business deserves software built for it — not rented, one-size-fits-all tools it has to bend around. We envision a world where the corner butcher, the local contractor, and the neighborhood clinic all run on custom apps and AI as capable as anything the enterprise has.</p>
          </motion.div>
          <motion.div 
            className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-8 shadow-xl shadow-gold-900/20"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="text-2xl font-bold text-gold-400 mb-4">Our Work</h2>
            <p className="text-gray-300">We've shipped field-service apps for contractors, AI inventory analysts for food retailers, HIPAA-architected platforms for healthcare, booking systems with live GPS tracking, and more — plus our own product, PlayMaker Pics, AI-powered sports photography. Different industries, one studio that delivers.</p>
          </motion.div>
        </div>

        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.a
            href="/contact"
            className="inline-block px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 text-black font-semibold rounded-xl shadow-lg shadow-gold-500/30 transition-all duration-300 pulse-glow"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start a Project
          </motion.a>
        </motion.div>
      </div>
      <motion.div 
        className="absolute top-0 left-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, -30, 0],
          y: [0, 30, 0],
        }}
        transition={{ 
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
    </div>
  );
};

export default About;
